import { apiFetch } from "@/lib/rateLimitNotice";

// 데스크톱 앱의 "브라우저에서 로그인" 딥링크 핸드오프. 서버 쪽 짝은
// server/core/src/modules/auth/desktop-handoff.ts.

// apps/desktop/src/main/env.ts 의 DEEP_LINK_SCHEME 과 값이 일치해야 한다.
const DEEP_LINK_SCHEME = "withtask";

// 핸드오프 딥링크로 되돌아갈 수 있는 스킴 화이트리스트. 랜딩(/auth/link)이 `cb`
// 쿼리로 받은 임의 문자열을 그대로 스킴에 붙이면 오픈 리다이렉트가 되므로, 우리가
// 아는 셸 스킴만 허용한다 — withtask(데스크톱 Electron), com.withtask.app(=appId,
// 네이티브 prod), com.withtask.app.dev(네이티브 develop). scripts/channel.mjs 의
// deepLinkScheme(=appId) 과 일치해야 한다.
//
// nightly 는 없다 — 딥링크 스킴을 갖는 네이티브 앱 채널은 channel.mjs 의 CHANNELS
// (prod·develop) 뿐이고, nightly 는 웹 전용 배포 채널(난수 도메인)이라 셸도 스킴도
// 없어 여기 들어갈 값이 없다. 네이티브 채널이 늘면 그 appId 를 여기에도 추가한다.
const ALLOWED_CALLBACK_SCHEMES = ["withtask", "com.withtask.app", "com.withtask.app.dev"];

/** 핸드오프 토큰을 담은 딥링크 URL. `scheme` 은 화이트리스트로 검증하고, 벗어나면
 * 데스크톱 기본 스킴으로 좁힌다(오픈 리다이렉트 방지). */
export function buildHandoffCallbackUrl(token: string, scheme: string = DEEP_LINK_SCHEME): string {
  const safeScheme = ALLOWED_CALLBACK_SCHEMES.includes(scheme) ? scheme : DEEP_LINK_SCHEME;
  return `${safeScheme}://auth-callback?token=${encodeURIComponent(token)}`;
}

/** 데스크톱(Electron) 전용 하위호환 래퍼. withtask:// 스킴 고정. */
export function buildDesktopCallbackUrl(token: string): string {
  return buildHandoffCallbackUrl(token, DEEP_LINK_SCHEME);
}

/**
 * 로그인된 브라우저 세션에서 1회용 핸드오프 토큰을 발급받는다. Electron이 이 토큰을
 * withtask:// 딥링크로 전달받아 verifyDesktopHandoffToken 으로 자신만의 새 세션을 얻는다.
 */
export async function generateDesktopHandoffToken(): Promise<string> {
  const res = await apiFetch("/api/auth/desktop-handoff/generate", { credentials: "include" });
  // 응답이 실패했는지
  const isResNotOk = !res.ok;
  if (isResNotOk) throw new Error(`핸드오프 토큰 발급 실패 (${res.status})`);
  const { token } = (await res.json()) as { token: string };
  return token;
}

/** Electron 쪽에서 딥링크로 받은 토큰을 제출해 새 세션 쿠키를 발급받는다. */
export async function verifyDesktopHandoffToken(token: string): Promise<boolean> {
  const res = await apiFetch("/api/auth/desktop-handoff/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token }),
  });
  return res.ok;
}

/**
 * 셸이 받은 `<scheme>://auth-callback?token=` 딥링크를 소비한다 — 토큰을 검증해 앱
 * 웹뷰의 자체 세션 쿠키를 확립하고, 성공하면 리로드해 App.tsx 세션 게이트를 다시 태운다.
 *
 * 딥링크를 "받는 통로"는 셸마다 다르다 — Electron 은 App.tsx 의 getDesktopBridge().
 * onDeepLink(preload IPC), Capacitor 는 PlatformBridge 의 @capacitor/app appUrlOpen
 * 이벤트. 통로는 런타임 API 도 다르고 번들 분할(@capacitor/* 를 웹 entry 에서 빼기)
 * 제약도 달라 한 리스너로 합칠 수 없지만, "받은 뒤 처리"는 동일하므로 여기 한 곳에 둔다.
 *
 * auth-callback 딥링크가 아니거나(다른 딥링크) 토큰이 없으면 아무 것도 하지 않고
 * null 을 돌려준다 — 호출측(네이티브)이 "핸드오프 딥링크였는지"로 시스템 브라우저를
 * 닫을지 판단할 수 있게, 검증 결과(boolean)와 "무관한 딥링크"(null)를 구분해 반환한다.
 *
 * @returns null=핸드오프 딥링크 아님, true=세션 확립(리로드 시작), false=검증 실패.
 */
export async function consumeHandoffDeepLink(url: string): Promise<boolean | null> {
  let token: string | null = null;
  try {
    const parsed = new URL(url);
    // auth-callback 딥링크가 아니면(푸시/유니버설 링크 등 다른 스킴 링크) 건드리지 않는다.
    if (parsed.host !== "auth-callback") return null;
    token = parsed.searchParams.get("token");
  } catch {
    return null;
  }
  if (!token) return null;
  const ok = await verifyDesktopHandoffToken(token);
  // 세션 쿠키를 받았으면 리로드로 App.tsx 세션 게이트를 다시 태운다.
  if (ok) window.location.reload();
  return ok;
}
